'use client'

import { AlertCircle, Check, Globe2, ShieldCheck } from 'lucide-react'
import type { ICompanyDomain } from '@/api/organization/company-domain/get-company-domain-query'
import { Button } from '@/components/ui/button'
import { GeneralModal, IconBox, ModalFooter } from '@/components/ui/general-modal'
import { getCompanyDomainAddUnavailableMessage } from '@/features/settings-modal/tabs/organization/utils/company-domain-claim'
import { cn } from '@/utils/styles/cn'

interface ICompanyDomainAddModalProps {
    open: boolean
    companyDomain: ICompanyDomain
    selectedDomain: string | null
    isSubmitting: boolean
    errorMessage: string | null
    onOpenChange: (open: boolean) => void
    onSelectedDomainChange: (domain: string) => void
    onConfirm: () => Promise<void>
}

/** Lets an administrator choose only from domain candidates proven by the backend. */
export function CompanyDomainAddModal({
    open,
    companyDomain,
    selectedDomain,
    isSubmitting,
    errorMessage,
    onOpenChange,
    onSelectedDomainChange,
    onConfirm,
}: ICompanyDomainAddModalProps) {
    const candidates = companyDomain.add.candidates
    const unavailableMessage = getCompanyDomainAddUnavailableMessage(
        companyDomain.add.unavailableReason,
        companyDomain.domains.length > 0
    )
    const showUnavailableMessage = candidates.length === 0 || !companyDomain.add.canAttempt

    return (
        <GeneralModal
            open={open}
            onOpenChange={onOpenChange}
            title="Add a company domain"
            subtitle="Choose a domain verified through a workspace administrator."
            icon={
                <IconBox>
                    <Globe2 className="h-5 w-5 text-stone-600" />
                </IconBox>
            }
        >
            {candidates.length > 0 ? (
                <div className="space-y-2" aria-label="Eligible company domains">
                    {candidates.map((candidate) => {
                        const isSelected = selectedDomain === candidate.name
                        return (
                            <button
                                key={candidate.name}
                                type="button"
                                aria-pressed={isSelected}
                                disabled={isSubmitting}
                                onClick={() => onSelectedDomainChange(candidate.name)}
                                className={cn(
                                    'flex w-full items-center gap-3 rounded-xl border px-3.5 py-3 text-left outline-none transition-[border-color,background-color,box-shadow] duration-150',
                                    'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60',
                                    isSelected
                                        ? 'border-stone-400 bg-stone-50 shadow-[0_0_0_1px_rgba(0,0,0,0.03)]'
                                        : 'border-stone-200 bg-white hover:border-stone-300 hover:bg-stone-50/70'
                                )}
                            >
                                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-stone-100">
                                    <ShieldCheck className="h-4 w-4 text-stone-600" aria-hidden="true" />
                                </div>
                                <div className="min-w-0 flex-1">
                                    <p className="truncate text-sm font-medium text-stone-900">{candidate.name}</p>
                                    <p className="mt-0.5 text-xs text-stone-500">
                                        {candidate.source === 'internal_test'
                                            ? 'Available for testing in this environment'
                                            : 'Verified administrator email'}
                                    </p>
                                </div>
                                <span
                                    className={cn(
                                        'flex h-5 w-5 shrink-0 items-center justify-center rounded-full border transition-colors',
                                        isSelected
                                            ? 'border-stone-900 bg-stone-900 text-white'
                                            : 'border-stone-300 bg-white text-transparent'
                                    )}
                                    aria-hidden="true"
                                >
                                    <Check className="h-3 w-3" />
                                </span>
                            </button>
                        )
                    })}
                </div>
            ) : null}

            {showUnavailableMessage && (
                <div
                    className={cn(
                        'flex items-start gap-3 rounded-xl border border-stone-200 bg-stone-50 px-4 py-3.5',
                        candidates.length > 0 && 'mt-3'
                    )}
                >
                    <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-stone-500" aria-hidden="true" />
                    <div>
                        <p className="text-sm font-medium text-stone-800">
                            {companyDomain.add.atLimit ? 'Domain limit reached' : 'No eligible domains'}
                        </p>
                        <p className="mt-1 text-sm leading-relaxed text-stone-600">{unavailableMessage}</p>
                    </div>
                </div>
            )}

            {errorMessage && (
                <div className="mt-3 flex items-start gap-2 rounded-lg border border-destructive/20 bg-destructive/5 px-3 py-2.5">
                    <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
                    <p className="text-sm text-destructive" role="alert">
                        {errorMessage}
                    </p>
                </div>
            )}

            <ModalFooter>
                <Button variant="ds2-secondary" size="ds2" disabled={isSubmitting} onClick={() => onOpenChange(false)}>
                    {companyDomain.add.canAttempt ? 'Cancel' : 'Close'}
                </Button>
                {companyDomain.add.canAttempt && candidates.length > 0 && (
                    <Button
                        variant="ds2-primary"
                        size="ds2"
                        disabled={!selectedDomain || !companyDomain.add.canAttempt || isSubmitting}
                        isLoading={isSubmitting}
                        onClick={() => void onConfirm()}
                    >
                        Add domain
                    </Button>
                )}
            </ModalFooter>
        </GeneralModal>
    )
}
